import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";
import saven from "../../Common/Images/save_n.gif";
import saveo from "../../Common/Images/save_o.gif";
import closen from "../../Common/Images/close_n.gif";
import closeo from "../../Common/Images/close_o.gif";

// Reusable Checkbox Component to keep the giant table clean
const RoleCheckbox = ({ label, field, roles, onChange }: { label: string, field: string, roles: any, onChange: (field: string, val: boolean) => void }) => (
    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <input
            type="checkbox"
            checked={!!roles[field]}
            onChange={(e) => onChange(field, e.target.checked)}
            style={{ margin: '0 8px 0 10px' }}
        />
        {label}
    </label>
);

// --- Define Props Interface ---
interface EL_SetLoginRolesProps {
    schoolCode: string;
    login: string;
    onClose: (shouldRefresh?: boolean) => void;
}

const EL_SetLoginRoles: React.FC<EL_SetLoginRolesProps> = ({ schoolCode, login, onClose }) => {

    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;


    const [familyCode, setFamilyCode] = useState<string>('A');
    const [loginExists, setLoginExists] = useState<boolean>(false);

    const [roles, setRoles] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState<boolean>(false);

    const [saveHover, setSaveHover] = useState(false);
    const [closeHover, setCloseHover] = useState(false);

    useEffect(() => {
        document.body.style.backgroundColor = 'azure'; // Dim the background
        if (schoolCode && login) {
            fetchRoles(schoolCode, login);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolCode, login]);

    const fetchRoles = async (school: string, userLogin: string) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/roles/get_user_roles?SchoolCode=${school}&Login=${userLogin}`);
            const json = await res.json();
            if (json.roles) {
                setRoles(json.roles);
                setLoginExists(json.exists);
                if (json.roles.FamilyCode) setFamilyCode(json.roles.FamilyCode);
            }
        } catch (err) {
            console.error("Failed to load roles");
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (field: string, value: boolean) => {
        setRoles(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/roles/save_user_roles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    Login: login,
                    FamilyCode: familyCode,
                    exists: loginExists,
                    roles: roles
                })
            });
            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                onClose(true);
            }
        } catch (err) {
            alert("Error saving roles");
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        popupWrapper: {
            width: "800px",
            background: "white",
            boxShadow: "0px 0px 12px rgba(0,0,0,0.5)",
            border: "2px solid red", // As requested in the legacy code string "borderstyle=solid 2px red"
            display: "flex",
            flexDirection: "column" as const,
            maxHeight: "90vh" // Prevent going off-screen
        },
        header: {
            backgroundImage: `url(${popimg}), url(${popbg})`,
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "left center, right center",
            backgroundSize: "30% 100%, 70% 100%",
            height: "22px", color: "white", padding: "6px 10px",
            fontWeight: "bold", fontSize: "14px", fontFamily: "Arial, sans-serif",
            display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #000"
        },
        scrollableContent: {
            overflowY: 'auto' as const,
            padding: '10px',
            backgroundColor: 'azure',
        },
        headerTable: { borderLeft: '1px solid black', borderRight: '1px solid black', borderTop: '1px solid black', paddingTop: '5px', width: '100%', margin: '0 auto', borderCollapse: 'collapse' as const },
        headerTd: { backgroundColor: '#C7DDFD', height: '25px', textAlign: 'center' as const, fontWeight: 'bold' },
        mainTable: { border: '1px solid black', fontSize: 'small', fontFamily: 'Arial', color: 'black', width: '100%', margin: '0 auto', borderCollapse: 'collapse' as const },
        thCol: { textDecoration: 'underline', borderRight: '1px solid black', padding: '5px', verticalAlign: 'top' as const, fontWeight: 'bold' },
        thColLast: { textDecoration: 'underline', padding: '5px', verticalAlign: 'top' as const, width: '33%', fontWeight: 'bold' },
        tdCol: { borderRight: '1px solid black', padding: '5px', verticalAlign: 'top' as const, width: '33%' },
        tdColLast: { padding: '5px', verticalAlign: 'top' as const, width: '33%' },
        btnRow: { borderTop: '1px solid black', padding: '10px', textAlign: 'center' as const }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.popupWrapper}>

                {/* Popup Header */}
                <div style={styles.header}>
                    <span>Edit Roles</span>
                    <img
                        src={popclose}
                        alt="close"
                        height="20"
                        style={{ cursor: "pointer" }}
                        onClick={() => onClose(false)}
                    />
                </div>

                <div style={styles.scrollableContent}>
                    {loading && <Loading />}

                    <table style={styles.headerTable}>
                        <tbody>
                            <tr><td style={styles.headerTd}>Edit roles for login</td></tr>
                        </tbody>
                    </table>

                    <table style={styles.mainTable}>
                        <tbody>
                            <tr>
                                <td style={styles.thCol}>Admin</td>
                                <td style={styles.thCol}>Sales</td>
                                <td style={styles.thColLast}>Finance</td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Register New Family" field="RegisterNewFamily" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Sale" field="ModifySale" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Payments Received" field="PaymentsReceived" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="List Families" field="ListFamilies" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Installation List" field="InstallationList" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Packages for dispatch" field="PackagesForDispatch" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Add Work Force" field="AddWorkForce" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Modify Dongle Status" field="ModifyDongleStatus" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Finance Sale" field="FinanceSale" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Approve Delivery Dockets" field="ApproveDeliveryDockets" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Dongle WorkFlow Report" field="DongleWorkFlowReport" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Invoice List" field="InvoiceList" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="List Of Data Captured From Payment Website" field="ListOfDataCapturedFromPaymentWebsite" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Sales Report" field="SalesReport" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Crelogix" field="Crelogix" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="List Subscriptions" field="ListSubscriptions" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}></td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="List Paypal Transactions" field="ListOfPaypalTransactions" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}></td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="List Paypal Notifications" field="ListOfPaypalNotifications" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.thCol}>Support</td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Commission Payment Details" field="CommissionPaymentDetails" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Customer Support - Entry" field="EntryCustomerSupport" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.thColLast}>CMS</td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="UsageAnalysis" field="UsageAnalysis" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Customer Support - View" field="ViewCustomerSupport" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Lead Collection" field="LeadCollection" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Order Packages" field="OrderPackages" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Search Engine" field="SearchEngine" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Telemarketing" field="Telemarketing" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Distributor Orders" field="DistributorOrders" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={{ ...styles.tdCol, verticalAlign: 'bottom' }}><RoleCheckbox label="DNC Search" field="DNCSearch" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Register/Modify Country Manager" field="CountryManager" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Add DNC" field="AddDNC" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Register / Modify Office" field="RegisterOffice" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="Add Points" field="AddPoints" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}></td><td style={styles.tdCol}></td><td style={styles.tdColLast}></td>
                            </tr>

                            <tr>
                                <td style={styles.thCol}>Maths Doctor</td>
                                <td style={styles.tdCol}><RoleCheckbox label="Edit Login" field="EditLogin_names" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.thColLast}>WLP</td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Strand/Substrand/Qns" field="StrandSubstrandQns" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.thCol}>Others</td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Default WLP running out - Maths" field="WLPRunningOutMaths" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Voucher Codes" field="VoucherCodes" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="View Lesson" field="ViewLesson" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Default WLP running out - English" field="WLPRunningOutEnglish" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="Maths Doctor- Maths Wiz Link" field="MathsDoctor" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}><RoleCheckbox label="View Games" field="ViewGames" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Maths Doctor WLP running out" field="WLPRunningOutMD" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="View Questions" field="ViewQuestions" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}></td>
                                <td style={styles.tdColLast}><RoleCheckbox label="Add Lessons To WLP" field="AddLessonsToWLP" roles={roles} onChange={handleRoleChange} /></td>
                            </tr>
                            <tr>
                                <td style={styles.tdCol}><RoleCheckbox label="View Exceptions" field="ViewExceptions" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={styles.tdCol}></td><td style={styles.tdColLast}></td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.tdCol, height: '30px' }}><RoleCheckbox label="Post Question Category" field="PostQuestionCategory" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={{ ...styles.tdCol, height: '30px' }}></td><td style={{ ...styles.tdColLast, height: '30px' }}></td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.tdCol, height: '30px' }}><RoleCheckbox label="ResetMD" field="ResetMD" roles={roles} onChange={handleRoleChange} /></td>
                                <td style={{ ...styles.tdCol, height: '30px' }}></td><td style={{ ...styles.tdColLast, height: '30px' }}></td>
                            </tr>

                            <tr>
                                <td colSpan={3} style={styles.btnRow}>
                                    <img
                                        src={saveHover ? saveo : saven}
                                        alt="Save"
                                        style={{
                                            cursor: "pointer",
                                            marginRight: "15px",
                                            verticalAlign: "middle"
                                        }}
                                        onMouseEnter={() => setSaveHover(true)}
                                        onMouseLeave={() => setSaveHover(false)}
                                        onClick={(e) => handleSave(e as any)}
                                    />

                                    <img
                                        src={closeHover ? closeo : closen}
                                        alt="Close"
                                        style={{
                                            cursor: "pointer",
                                            verticalAlign: "middle"
                                        }}
                                        onMouseEnter={() => setCloseHover(true)}
                                        onMouseLeave={() => setCloseHover(false)}
                                        onClick={() => onClose(false)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default EL_SetLoginRoles;